import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
// Import Shared Components
import {
  Loader,
  ErrorComponent,
  Table,
  Dropdown,
  DatePicker,
  ButtonGroup,
  IconButton,
} from 'shared-components';
// Import Shared Utils
import { get, deleteHTTP } from 'shared-components/src/utils/http';
import {
  getEndOfDay,
  subtractMonth,
  currentDateTime,
  getStartAndEndDates,
} from 'shared-components/src/utils/dates';
import { getFormattedAccountType } from 'shared-components/src/utils/cardholders';
import { formatDateForTransactionQuery } from 'shared-components/src/utils/formatDateTime';
// Import Global Components
import Heading from '../../../../components/Heading';
import Alert from '../../../../components/Alert';
import Label from '../../../../components/Label';
import Button from '../../../../components/Button';
// Import Local Components
import AddExternalAccountModal from '../../components/AddExternalAccountModal';
import EditExternalAccountModal from '../../components/EditExternalAccountModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
// Import Component Styles
import { HeadingTabs, HeadingWrapper } from '../../styles';
import { TypeBadge, FilterContainer, ButtonsContainer } from './styles';
import { TransactionsHeader, DatesContainer } from '../../../Home/styles';

const ExternalAccounts = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const theme = useContext(ThemeContext);

  const page = 'external-accounts';

  // Component states
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [alert, setAlert] = useState(null);

  // Type filter state
  const [selectedType, setSelectedType] = useState('');

  // Date filter states
  const [startDate, setStartDate] = useState(
    subtractMonth(currentDateTime(), 1)
  );
  const [endDate, setEndDate] = useState(getEndOfDay());
  const [focusedInput, setFocusedInput] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const createIntl = key =>
    intl.formatMessage({
      id: `date-${key}-label`,
      defaultMessage: intl.messages[`date-${key}-label`],
      description: `Date ${key} label`,
    });

  // Type options for filter dropdown
  const typeFilterOptions = [
    { key: '', val: intl.messages[`${page}-type-all`] || 'All Types' },
    {
      key: 'external_funding_account',
      val: intl.messages[`${page}-type-push`] || 'Interac e-Transfer Push',
    },
    {
      key: 'external_source_account',
      val: intl.messages[`${page}-type-pull`] || 'Interac e-Transfer Pull',
    },
    {
      key: 'personal_funding_account',
      val: intl.messages[`${page}-type-visa-direct`] || 'Debit Card - Push',
    },
    {
      key: 'personal_source_account',
      val: intl.messages[`${page}-type-interac`] || 'Debit Card - Pull',
    },
  ];

  // Fetch external accounts
  const fetchExternalAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Add date filters
      params.append('start_date', formatDateForTransactionQuery(startDate));
      params.append('end_date', formatDateForTransactionQuery(endDate));

      // If a specific type is selected, use it; otherwise use both types
      if (selectedType) {
        params.append('type', selectedType);
      } else {
        params.append(
          'type',
          'external_funding_account,external_source_account,personal_funding_account,personal_source_account'
        );
      }

      const externalAccountsData = await get(
        `/api/v2/cardholder_portal/cardholder/external_accounts?${params.toString()}`
      );

      // sort by created_at descending
      const sortedExternalAccounts = (externalAccountsData.data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setExternalAccounts(sortedExternalAccounts);
    } catch (e) {
      setError({ type: 'danger', message: e });
    }
    setLoading(false);
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchExternalAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, startDate, endDate]);

  // Format type for display
  const formatAccountType = type => getFormattedAccountType(type);

  // Handle account added
  const handleAccountAdded = newAccount => {
    // check if the new account matches the current filters
    if (
      (selectedType === '' ||
        selectedType === newAccount.type) /* type filter */ &&
      new Date(newAccount.created_at) >= startDate /* start date filter */ &&
      new Date(newAccount.created_at) <= endDate /* end date filter */
    ) {
      setExternalAccounts(prev => [newAccount, ...prev]);
    }
  };

  // Handle account updated
  const handleAccountUpdated = updatedAccount => {
    setExternalAccounts(prev =>
      prev.map(acc => (acc.id === updatedAccount.id ? updatedAccount : acc))
    );
  };

  // Edit account
  const editExternalAccount = (e, account) => {
    e.preventDefault();
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  // Delete account
  const deleteExternalAccount = (e, account) => {
    e.preventDefault();
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const closeConfirmDeleteModal = e => {
    e.preventDefault();
    setShowDeleteModal(false);
    setSelectedAccount(null);
  };

  // Handle delete
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteHTTP(
        `/api/v2/cardholder_portal/cardholder/external_accounts/${selectedAccount.id}`
      );
      setExternalAccounts(prev =>
        prev.filter(acc => acc.id !== selectedAccount.id)
      );
      setAlert({
        type: 'success',
        message:
          intl.messages[`${page}-account-deleted-alert`] ||
          'External account deleted successfully',
      });
      setShowDeleteModal(false);
      setSelectedAccount(null);
    } catch (e) {
      setAlert({
        type: 'danger',
        message:
          e.message ||
          intl.messages[`${page}-delete-error-alert`] ||
          'Failed to delete external account',
      });
      setShowDeleteModal(false);
    }
    setDeleteLoading(false);
  };

  // Define page states

  const columns = [
    {
      Header: () => (
        <FormattedMessage
          id="name-table-heading"
          description="Table Header name"
          defaultMessage={intl.messages['name-table-heading'] || 'Name'}
        />
      ),
      accessor: 'name',
    },
    {
      Header: () => (
        <FormattedMessage
          id="type-table-heading"
          description="Table Header type"
          defaultMessage={intl.messages['type-table-heading'] || 'Type'}
        />
      ),
      accessor: 'type',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => (
        <TypeBadge $type={value}>{formatAccountType(value)}</TypeBadge>
      ),
    },
    {
      Header: () => (
        <FormattedMessage
          id="create-at-table-heading"
          description="Table Header created at"
          defaultMessage={intl.messages['create-at-table-heading'] || 'Created'}
        />
      ),
      accessor: 'created_at',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <FormattedDate value={value} />,
    },
    {
      Header: () => (
        <FormattedMessage
          id="actions-table-heading"
          description="Table Header actions"
          defaultMessage={intl.messages['actions-table-heading'] || ''}
        />
      ),
      accessor: 'action',
      innerFocus: true,
      // eslint-disable-next-line react/prop-types
      Cell: ({ original }) => (
        <ButtonGroup justifyContent="flex-start">
          <IconButton
            iconType="external"
            onClick={e => {
              e.preventDefault();
              navigate(`${original.id}`);
            }}
            color={theme.colors.lightBlue}
            title={
              intl.messages[`${page}-view-transfers-button`] ||
              'View Account Transfers'
            }
          />
          <IconButton
            iconType="edit"
            onClick={e => editExternalAccount(e, original)}
            color={theme.colors.lightBlue}
            title={
              intl.messages[`${page}-edit-button`] || 'Edit External Account'
            }
          />
          <IconButton
            iconType="trash"
            onClick={e => deleteExternalAccount(e, original)}
            color={theme.colors.danger}
            title={
              intl.messages[`${page}-delete-button`] ||
              'Delete External Account'
            }
          />
        </ButtonGroup>
      ),
    },
  ];
  if (error) return <ErrorComponent />;
  return (
    <>
      <HeadingWrapper>
        <HeadingTabs>
          <Link to="/transfers">
            <Heading
              page="transfer"
              heading={intl.messages['transfer-heading']}
            />
          </Link>
          <Link to="/transfers/transfer-history">
            <Heading
              page="transfer-history"
              heading={intl.messages['transfer-history-heading']}
            />
          </Link>
          <Heading
            page={page}
            heading={intl.messages[`${page}-heading`] || 'External Accounts'}
            className="active"
          />
        </HeadingTabs>
      </HeadingWrapper>
      {alert && (
        <Alert
          type={alert.type}
          alert={alert.message}
          alertFor="action-result"
          page={page}
        />
      )}
      <TransactionsHeader>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <FilterContainer>
            <Label
              labelFor="type-filter"
              page={page}
              label={
                intl.messages[`${page}-type-filter-label`] || 'Filter by type:'
              }
            />
            <Dropdown
              options={typeFilterOptions}
              selected={selectedType}
              updateItem={val => setSelectedType(val)}
            />
          </FilterContainer>
          <DatesContainer>
            <DatePicker
              toPlaceholder="MM/DD/YYYY"
              fromPlaceholder="MM/DD/YYYY"
              startDate={startDate}
              endDate={endDate}
              onDatesChange={({ startDate: newStart, endDate: newEnd }) => {
                const { validStartDate, validEndDate } = getStartAndEndDates(
                  newStart,
                  newEnd
                );
                setStartDate(validStartDate);
                setEndDate(validEndDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={input => {
                setFocusedInput(input);
              }}
              isOutsideRange={() => false}
              showDefaultInputIcon
              toText={createIntl('to')}
              fromText={createIntl('from')}
              label="true"
            />
          </DatesContainer>
        </div>
        <ButtonsContainer>
          <Button
            buttonText={
              intl.messages[`${page}-add-button`] || 'Add External Account'
            }
            buttonFor="add"
            page={page}
            onClick={() => setShowAddModal(true)}
            type="primary"
          />
        </ButtonsContainer>
      </TransactionsHeader>
      {loading && <Loader />}
      {!loading && externalAccounts.length === 0 && (
        <Alert
          type="info"
          alert={
            intl.messages[`${page}-no-accounts-alert`] ||
            'No external accounts found.'
          }
          alertFor="no-accounts"
          page={page}
        />
      )}
      {!loading && externalAccounts.length > 0 && (
        <Table
          data={externalAccounts}
          columns={columns}
          defaultPageSize={10}
          showPagination={externalAccounts.length > 10}
        />
      )}

      {/* Add External Account Modal */}
      {showAddModal && (
        <AddExternalAccountModal
          toggleModal={setShowAddModal}
          setSuccess={setAlert}
          onAccountAdded={handleAccountAdded}
        />
      )}

      {/* Edit External Account Modal */}
      {showEditModal && selectedAccount && (
        <EditExternalAccountModal
          toggleModal={setShowEditModal}
          setSuccess={setAlert}
          onAccountUpdated={handleAccountUpdated}
          id={selectedAccount.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAccount && (
        <ConfirmDeleteModal
          toggleModal={closeConfirmDeleteModal}
          loading={deleteLoading}
          alert={null}
          deleteItemValue={String(selectedAccount.id)}
          deleteItemName={selectedAccount.name}
          deleteCallback={handleDelete}
          page={page}
        />
      )}
    </>
  );
};

export default ExternalAccounts;
